import React, { useState } from 'react';
import { Send, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { useLanguage, usePageMeta } from '../context/LanguageContext';
const EMAIL = 'Medinaproduction20@gmail.com';
const ENDPOINT = '/api/send-consulting-request';
const fieldLabel = 'block text-navy text-sm font-semibold mb-2';
const fieldInput = 'w-full rounded-lg border border-borderColor bg-white px-4 py-3 text-sm text-navy placeholder-gray-400 focus:outline-none focus:border-brandOrange focus:ring-1 focus:ring-brandOrange transition-colors';
interface FormState {
  fullName: string;
  company: string;
  email: string;
  phone: string;
  service: string;
  message: string;
}
const EMPTY: FormState = {
  fullName: '',
  company: '',
  email: '',
  phone: '',
  service: '',
  message: ''
};
export function Contact() {
  const {
    t
  } = useLanguage();
  const c = t.contactPage;
  usePageMeta(t.meta.contact.title, t.meta.contact.description);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const submitted = status === 'success';
  const update = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(prev => ({
      ...prev,
      [key]: e.target.value
    }));
    setErrors(prev => ({
      ...prev,
      [key]: undefined
    }));
  };
  const validate = (): boolean => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.fullName.trim()) next.fullName = c.validation.fullName;
    if (!form.company.trim()) next.company = c.validation.company;
    if (!form.email.trim()) next.email = c.validation.email;else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = c.validation.emailInvalid;
    if (!form.service) next.service = c.validation.service;
    if (!form.message.trim()) next.message = c.validation.message;
    setErrors(next);
    return Object.keys(next).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Prevent duplicate submissions while a request is in flight.
    if (status === 'sending') return;
    if (!validate()) return;
    setStatus('sending');
    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });
      // Always read the body so we can surface the real server-side reason.
      const result = await res.json().catch(() => ({}));
      if (!res.ok) {
        console.error('Contact form submission failed:', {
          status: res.status,
          ...result
        });
        throw new Error(result?.detail || result?.error || 'Request failed');
      }
      setStatus('success');
      setForm(EMPTY);
    } catch (err) {
      console.error('Contact form error:', err);
      setStatus('error');
    }
  };
  const errorText = 'text-red-600 text-xs mt-1.5';
  const sending = status === 'sending';
  return <div className="w-full bg-white" data-id="element-174">
      {/* Section 1 — Hero */}
      <section className="w-full bg-navy pt-16 pb-24" data-id="element-175">
        <div className="max-w-6xl mx-auto px-6 lg:px-8" data-id="element-176">
          <h1 className="text-4xl md:text-5xl lg:text-[52px] font-poppins font-bold text-white mb-5" data-id="element-177">
            {c.heroTitle}
          </h1>
          <p className="text-blue-100 text-lg leading-relaxed max-w-2xl" data-id="element-178">
            {c.heroSubtitle}
          </p>
        </div>
      </section>

      {/* Section 2 — Contact Form */}
      <section className="w-full py-16" data-id="element-179">
        <div className="max-w-3xl mx-auto px-6 lg:px-8" data-id="element-180">
          <div className="bg-white border border-borderColor rounded-2xl p-8 md:p-10 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.12)]" data-id="element-181">
            {submitted && <div className="flex items-start gap-3 bg-green-50 border border-green-200 text-green-800 rounded-lg px-4 py-3 mb-6 text-sm" data-id="element-182">
                <CheckCircle2 className="w-5 h-5 shrink-0 text-green-600 mt-0.5" data-id="element-183" />
                <span data-id="element-184">{c.success}</span>
              </div>}
            {status === 'error' && <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3 mb-6 text-sm" data-id="element-185">
                <AlertCircle className="w-5 h-5 shrink-0 text-red-600 mt-0.5" data-id="element-186" />
                <span data-id="element-187">{c.error}</span>
              </div>}
            <form onSubmit={handleSubmit} className="space-y-6" noValidate data-id="element-188">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-189">
                <div data-id="element-190">
                  <label htmlFor="fullName" className={fieldLabel} data-id="element-191">
                    {c.labels.fullName}
                  </label>
                  <input id="fullName" type="text" placeholder={c.placeholders.fullName} value={form.fullName} onChange={update('fullName')} className={fieldInput} data-id="element-192" />
                  {errors.fullName && <p className={errorText} data-id="element-193">{errors.fullName}</p>}
                </div>
                <div data-id="element-194">
                  <label htmlFor="company" className={fieldLabel} data-id="element-195">
                    {c.labels.company}
                  </label>
                  <input id="company" type="text" placeholder={c.placeholders.company} value={form.company} onChange={update('company')} className={fieldInput} data-id="element-196" />
                  {errors.company && <p className={errorText} data-id="element-197">{errors.company}</p>}
                </div>
                <div data-id="element-198">
                  <label htmlFor="email" className={fieldLabel} data-id="element-199">
                    {c.labels.email}
                  </label>
                  <input id="email" type="email" placeholder={c.placeholders.email} value={form.email} onChange={update('email')} className={fieldInput} data-id="element-200" />
                  {errors.email && <p className={errorText} data-id="element-201">{errors.email}</p>}
                </div>
                <div data-id="element-202">
                  <label htmlFor="phone" className={fieldLabel} data-id="element-203">
                    {c.labels.phone}
                  </label>
                  <input id="phone" type="tel" placeholder={c.placeholders.phone} value={form.phone} onChange={update('phone')} className={fieldInput} data-id="element-204" />
                </div>
              </div>

              <div data-id="element-205">
                <label htmlFor="service" className={fieldLabel} data-id="element-206">
                  {c.labels.service}
                </label>
                <select id="service" value={form.service} onChange={update('service')} className={`${fieldInput} appearance-none bg-white`} data-id="element-207">
                  <option value="" data-id="element-208">{c.serviceDefault}</option>
                  {c.serviceOptions.map(option => <option key={option} value={option} data-id="element-209">
                      {option}
                    </option>)}
                </select>
                {errors.service && <p className={errorText} data-id="element-210">{errors.service}</p>}
              </div>

              <div data-id="element-211">
                <label htmlFor="message" className={fieldLabel} data-id="element-212">
                  {c.labels.message}
                </label>
                <textarea id="message" rows={6} placeholder={c.placeholders.message} value={form.message} onChange={update('message')} className={`${fieldInput} resize-y`} data-id="element-213" />
                {errors.message && <p className={errorText} data-id="element-214">{errors.message}</p>}
              </div>

              <button type="submit" disabled={sending} className="w-full flex items-center justify-center gap-2 bg-brandOrange hover:bg-brandOrange-hover text-white py-3.5 rounded-lg font-semibold transition-colors disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-215">
                {sending ? <>
                    <Loader2 className="w-4 h-4 animate-spin" data-id="element-216" />
                    {c.sending}
                  </> : <>
                    <Send className="w-4 h-4" data-id="element-217" />
                    {c.submit}
                  </>}
              </button>
            </form>
          </div>

          {/* Section 3 — Contact Email */}
          <p className="text-center text-gray-500 text-sm mt-8" data-id="element-218">
            {c.emailDirect}{' '}
            <a href={`mailto:${EMAIL}`} className="text-navy font-bold hover:text-brandOrange transition-colors" data-id="element-219">
              {EMAIL}
            </a>
          </p>
        </div>
      </section>
    </div>;
}